import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Chip,
  Paper,
  FormControlLabel,
  Checkbox,
  Alert
} from '@mui/material';
import axios from 'axios';
import { 
  TrendingUp, 
  Warning,
  CheckCircleOutline,
  ErrorOutline 
} from '@mui/icons-material';
// import { useAuth } from '../../context/AuthContext';
import TierComparisonTable from './TierComparisonTable';
import UsageImpactPreview from './UsageImpactPreview';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import NotificationSnackbar from '../common/NotificationSnackbar';

const steps = ['Preview Changes', 'Confirm Changes'];

function ServiceTierChangeModal({ 
  open, 
  onClose, 
  currentTier, 
  newTier, 
  onSuccess 
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [userAgreed, setUserAgreed] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    description: '',
    severity: 'success'
  });
  // const { fetchUserProfile } = useAuth();

  useEffect(() => {
    if (open && newTier) {
      loadPreviewData();
    }
  }, [open, newTier]);

  useEffect(() => {
    // Reset user agreement when returning to first step
    if (activeStep === 0) {
      setUserAgreed(false);
    }
  }, [activeStep]);

  const showNotification = (message, description, severity = 'success') => {
    setNotification({
      open: true,
      message,
      description,
      severity
    });
  };

  const handleNotificationClose = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const loadPreviewData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/api/auth/service-tier/preview?newTier=${newTier}`);
      console.log("PREVIEW DATA", response.data.data);
      setPreviewData(response.data.data);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to load preview data';
      setError(errorMsg);
      showNotification('Failed to Load Preview', errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!userAgreed) {
      showNotification(
        'Confirmation Required',
        'Please agree to the terms before confirming',
        'warning'
      );
      return;
    }

    // Call the parent's onSuccess handler to initiate Stripe checkout
    onSuccess?.();
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleClose = () => {
    setActiveStep(0);
    setPreviewData(null);
    setUserAgreed(false);
    setError(null);
    onClose();
  };

  // Helper function to determine if this is a downgrade
  const isDowngrade = () => {
    if (!previewData) return false;
    return previewData.newTier.pricing.basePrice < previewData.currentTier.pricing.basePrice;
  };

  const renderImpactSection = () => {
    if (!previewData) return null;

    const { impact } = previewData;
    const isDowngrading = isDowngrade();

    return (
      <>
        <UsageImpactPreview impact={impact} />

        {/* Pricing Impact */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Pricing Impact
            <TrendingUp sx={{ ml: 1, verticalAlign: 'bottom' }} />
          </Typography>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography>Current Monthly Price</Typography>
                  <Typography variant="h6">€{(previewData.currentTier.pricing.basePrice / 100).toFixed(2)}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography>New Monthly Price</Typography>
                  <Typography variant="h6">€{(previewData.newTier.pricing.basePrice / 100).toFixed(2)}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography>Monthly Difference</Typography>
                  <Typography 
                    variant="h6" 
                    color={impact.pricing.difference > 0 ? 'error.main' : 'success.main'}
                  >
                    {impact.pricing.difference > 0 ? '+' : ''}€{(impact.pricing.difference / 100).toFixed(2)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* Downgrade Warning */}
        {isDowngrading && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Important Notice About Downgrading
            </Typography>
            <Typography variant="body2">
              • Your current subscription will remain active until the end of the billing period
            </Typography>
            <Typography variant="body2">
              • The new lower tier will take effect at the start of your next billing cycle
            </Typography>
            <Typography variant="body2">
              • You will still have access to all features until the current period ends
            </Typography>
          </Alert>
        )}
      </>
    );
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={loading ? undefined : handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {isDowngrade() ? 'Downgrade' : 'Change'} Service Tier
          <Stepper activeStep={activeStep} sx={{ mt: 2, maxWidth: '50%', marginLeft: 'auto', marginRight: 'auto', mb: 2 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </DialogTitle>

        <DialogContent>
          {error && (
            <ErrorState
              error={error}
              variant="alert"
              onRetry={activeStep === 0 ? loadPreviewData : handleConfirm}
              onClose={() => setError(null)}
            />
          )}

          {loading ? (
            <LoadingState 
              message={
                activeStep === 0 
                  ? "Loading preview data..." 
                  : "Applying tier changes..."
              }
            />
          ) : activeStep === 0 ? (
            <>
              <Typography>
                You are about to {isDowngrade() ? 'downgrade' : 'change'} your service tier from <strong>{currentTier}</strong> to <strong>{newTier}</strong>.
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              {previewData ? (
                <>
                  <TierComparisonTable 
                    currentTier={previewData.currentTier} 
                    newTier={previewData.newTier} 
                  />
                  {renderImpactSection()}
                </>
              ) : !error && (
                <LoadingState 
                  variant="inline"
                  message="Loading tier information..."
                  size={24}
                />
              )}
            </>
          ) : (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Confirm Your Changes
              </Typography>
              <Typography>
                Please review the changes carefully. This action will:
              </Typography>
              <Box sx={{ mt: 2, textAlign: 'left' }}>
                <ul>
                  <li>{isDowngrade() ? 'Downgrade' : 'Change'} your service tier to {newTier}</li>
                  <li>Update your monthly API call limits</li>
                  {isDowngrade() && (
                    <li>Take effect at the start of your next billing cycle</li>
                  )}
                </ul>
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={userAgreed}
                    onChange={(e) => setUserAgreed(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    I understand and agree to these changes
                  </Typography>
                }
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          {activeStep === 0 ? (
            <Button onClick={handleNext} disabled={loading}>
              Next
            </Button>
          ) : (
            <Button
              onClick={handleConfirm}
              disabled={loading || !userAgreed}
              variant="contained"
              color="primary"
            >
              Confirm {isDowngrade() ? 'Downgrade' : 'Changes'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        description={notification.description}
        severity={notification.severity}
        onClose={handleNotificationClose}
      />
    </>
  );
}

export default ServiceTierChangeModal; 